import { getApps, initializeApp } from "firebase/app";
import {
  initializeAnalytics,
  isSupported,
  logEvent,
  type Analytics,
} from "firebase/analytics";

type AnalyticsEventParams = Record<
  string,
  string | number | boolean | undefined
>;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let analyticsPromise: Promise<Analytics | null> | null = null;

function hasAnalyticsConfig() {
  return Object.values(firebaseConfig).every((value) => Boolean(value));
}

function getFirebaseApp() {
  return getApps()[0] ?? initializeApp(firebaseConfig);
}

function cleanParams(params: AnalyticsEventParams) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
}

export function getSiteAnalytics() {
  if (typeof window === "undefined" || !hasAnalyticsConfig()) {
    return Promise.resolve(null);
  }

  analyticsPromise ??= isSupported()
    .then((supported) => {
      if (!supported) return null;

      return initializeAnalytics(getFirebaseApp(), {
        config: {
          send_page_view: false,
        },
      });
    })
    .catch(() => null);

  return analyticsPromise;
}

export async function trackEvent(
  eventName: string,
  params: AnalyticsEventParams = {},
) {
  const analytics = await getSiteAnalytics();
  if (!analytics) return;

  logEvent(analytics, eventName, cleanParams(params));
}

export function trackPageView(path: string) {
  return trackEvent("page_view", {
    page_title: document.title,
    page_location: window.location.href,
    page_path: path,
  });
}

export function trackContentPageView({
  path,
  contentType,
  contentId,
  contentTitle,
  contentCategory,
}: {
  path: string;
  contentType: "project" | "blog_post";
  contentId: string;
  contentTitle: string;
  contentCategory: string;
}) {
  return trackEvent("page_view", {
    page_title: contentTitle,
    page_location: window.location.href,
    page_path: path,
    content_type: contentType,
    content_id: contentId,
    content_title: contentTitle,
    content_category: contentCategory,
  });
}

export function trackProjectOpen(projectId: string, projectTitle: string) {
  return trackEvent("select_project", {
    project_id: projectId,
    project_title: projectTitle,
  });
}

export function trackBlogPostOpen(postId: string, postTitle: string) {
  return trackEvent("select_blog_post", {
    post_id: postId,
    post_title: postTitle,
  });
}
